import React, { useMemo, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, Download, Pencil, Eye, RotateCcw, FileSpreadsheet, Info, FileArchive, Save, Loader2, AlertTriangle, Check } from 'lucide-react';
import type { ExcelFaturaItem, ExcelErroItem, OrigemInjecao } from './faturaGridModel';
import type { CorrecoesPlanilha } from '../../../../types';
import { abreComposicaoInjecao, alvoNaAbaFaturas, buildAllGrids, buildResumoGrid, chaveCorrecao, escalaresDeCorrecoes, VERSAO_CORRECOES } from './faturaGridModel';
import { useUcsGeradorasVigor } from './ucsGeradorasVigor';
import InjecoesFaturaModal from './InjecoesFaturaModal';
import { writeWorkbook } from '../excelExport';
import { useGridEdits, type TabKey } from './useGridEdits';
import SpreadsheetGrid from './SpreadsheetGrid';
import { baixarPdfsZip } from '../zipFaturas';
import { salvarCorrecoesPlanilha, type CorrecaoExtracao } from '../../../../src/services/extracaoApi';
import { useAuth } from '../../../../src/contexts/AuthContext';
import { useToast } from '../../../../src/contexts/ToastContext';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  faturas: ExcelFaturaItem[];
  erros?: ExcelErroItem[];
  titulo?: string;
  /** Avisa o pai do que foi gravado, por `extracoes_faturas.id`, para ele atualizar
   *  a lista/cache sem um novo round-trip. */
  onCorrecoesSalvas?: (porExtracao: Record<string, CorrecoesPlanilha>) => void;
}

type StatusFiltro = 'todos' | 'ok' | 'revisar' | 'falhou';

// Referência estável: sem isto o default `erros = []` viria novo a cada render,
// refazendo os grids e zerando as edições em cada tecla digitada.
const EMPTY_ERROS: ExcelErroItem[] = [];

/** Abas da TELA. `resumo` é uma projeção da aba Faturas — não é uma aba da camada de
 *  correção (essa é por `TabKey`, que espelha o que o banco guarda). O que se edita no
 *  Resumo é gravado na aba Faturas, na coluna de origem. */
type ViewTab = TabKey | 'resumo';
interface TabCfg { key: ViewTab; label: string; freezeCount: number; freezeWidths: number[]; rail?: boolean }
const TABS: TabCfg[] = [
  { key: 'resumo', label: 'Resumo', freezeCount: 2, freezeWidths: [170, 140], rail: true },
  // Faturas: coluna 1 = Motivo Revisão (âmbar), depois UC e Nome — as 3 congeladas.
  { key: 'faturas', label: 'Faturas', freezeCount: 3, freezeWidths: [230, 150, 200], rail: true },
  { key: 'itens', label: 'Itens', freezeCount: 2, freezeWidths: [190, 120] },
  { key: 'medidor', label: 'Medidor', freezeCount: 2, freezeWidths: [190, 120] },
  { key: 'auditoria', label: 'Auditoria', freezeCount: 2, freezeWidths: [190, 120] },
];

const FILTROS: { key: StatusFiltro; rotulo: string; classe: string }[] = [
  { key: 'todos', rotulo: 'Todos', classe: 'text-gray-600 border-gray-300' },
  { key: 'ok', rotulo: 'Confere', classe: 'text-emerald-700 border-emerald-300' },
  { key: 'revisar', rotulo: 'Revisar', classe: 'text-amber-700 border-amber-300' },
  { key: 'falhou', rotulo: 'Falhou', classe: 'text-red-600 border-red-300' },
];

const PlanilhaFaturasModal: React.FC<Props> = ({ isOpen, onClose, faturas, erros = EMPTY_ERROS, titulo, onCorrecoesSalvas }) => {
  const { user, profile } = useAuth();
  const { addToast } = useToast();
  const [statusFiltro, setStatusFiltro] = useState<StatusFiltro>('todos');
  const [editable, setEditable] = useState(false);
  const [tab, setTab] = useState<ViewTab>('faturas');
  const [zipProgresso, setZipProgresso] = useState<{ feitos: number; total: number } | null>(null);
  const [salvando, setSalvando] = useState(false);
  // Ação represada por alteração não salva: fechar o modal ou trocar o filtro de
  // status (que remonta o grid a partir de outro conjunto de faturas).
  const [confirmacao, setConfirmacao] = useState<{ tipo: 'sair' } | { tipo: 'filtro'; alvo: StatusFiltro } | null>(null);
  // Linha (índice na grade) cuja composição de injeção está aberta no mini modal.
  const [injecoesDaLinha, setInjecoesDaLinha] = useState<number | null>(null);
  const zipCancelRef = useRef(false);

  // Sem as UCs das usinas não dá para dizer de quem é a energia: o Resumo então mostra a
  // injeção numa coluna só, como antes (ver `buildResumoGrid`).
  const ucsVigor = useUcsGeradorasVigor(isOpen);

  const contagens = useMemo(() => {
    const c = { todos: faturas.length, ok: 0, revisar: 0, falhou: 0 } as Record<StatusFiltro, number>;
    for (const f of faturas) { const s = (f.reconciliacao?.status || 'falhou') as 'ok' | 'revisar' | 'falhou'; c[s]++; }
    return c;
  }, [faturas]);

  const faturasFiltradas = useMemo(
    () => statusFiltro === 'todos' ? faturas : faturas.filter(f => (f.reconciliacao?.status || 'falhou') === statusFiltro),
    [faturas, statusFiltro],
  );

  const source = useMemo(() => buildAllGrids(faturasFiltradas, erros), [faturasFiltradas, erros]);
  const edits = useGridEdits(source);

  // Projeção da aba Faturas JÁ editada: o que se corrige lá aparece aqui na hora.
  const resumo = useMemo(() => buildResumoGrid(edits.grids.faturas, ucsVigor), [edits.grids.faturas, ucsVigor]);
  const linhasDe = (k: ViewTab) => (k === 'resumo' ? resumo?.rows.length ?? 0 : source[k]?.rows.length ?? 0);

  const available = TABS.filter(t => linhasDe(t.key) > 0);
  const activeTab: ViewTab = available.some(t => t.key === tab) ? tab : (available[0]?.key ?? 'faturas');
  const cfg = TABS.find(t => t.key === activeTab)!;
  const noResumo = activeTab === 'resumo';
  // Editar no Resumo é editar a aba Faturas: a projeção não tem estado próprio, ela só
  // reflete a coluna de origem. `origemCols[c]` é essa coluna; as de injeção são somatórios
  // e o destino depende da linha (ver `alvoNaAbaFaturas`).
  const abaEdicao: TabKey = noResumo ? 'faturas' : activeTab;
  const origemCols = noResumo ? resumo?.origemCols : undefined;
  const grid = noResumo ? resumo : edits.grids[activeTab as TabKey];

  // Coluna da aba Faturas → colunas do Resumo que a espelham. Só existe no Resumo, e é
  // o que traduz as marcas de célula corrigida para a projeção (a linha é a mesma).
  const destinoResumo = useMemo(() => {
    if (!origemCols) return null;
    const m = new Map<number, number[]>();
    origemCols.forEach((oc, c) => { if (oc !== null) m.set(oc, [...(m.get(oc) || []), c]); });
    return m;
  }, [origemCols]);

  /** Coluna do Resumo de cada origem de injeção (o inverso de `injecaoCols`). */
  const colDaOrigem = useMemo(() => {
    const m = new Map<OrigemInjecao, number>();
    resumo?.injecaoCols.forEach((origem, c) => m.set(origem, c));
    return m;
  }, [resumo]);

  const parcelasDaLinha = (r: number) => resumo?.parcelas[r] || [];

  const alvoDaCelula = (r: number, c: number) => (noResumo && resumo ? alvoNaAbaFaturas(resumo, r, c) : c);
  const acaoDaCelula = (r: number, c: number) =>
    noResumo && resumo && abreComposicaoInjecao(resumo, r, c) ? () => setInjecoesDaLinha(r) : null;

  const projetar = (set: Set<string>) => {
    if (!destinoResumo || !resumo) return set;
    const out = new Set<string>();
    for (const chave of set) {
      const [rs, cs] = chave.split(':');
      const r = Number(rs), c = Number(cs);
      for (const cr of destinoResumo.get(c) || []) out.add(`${r}:${cr}`);
      // Correção numa parcela de injeção (kWh ou UC geradora) marca a célula da origem
      // para onde ela conta — é a única pista visual de que aquele total foi mexido.
      const parcela = parcelasDaLinha(r).find(pp => pp.colQtd === c || pp.colUc === c);
      const cr = parcela ? colDaOrigem.get(parcela.origem) : undefined;
      if (cr !== undefined) out.add(`${r}:${cr}`);
    }
    return out;
  };

  /** Como a fatura se identifica no mini modal — o que já está na tela (com correção
   *  aplicada), não uma segunda leitura do PDF. */
  const rotuloDaLinha = (r: number) => {
    const valor = (rotulo: string) => {
      const c = resumo?.columns.findIndex(col => col.label === rotulo) ?? -1;
      return c >= 0 ? String(resumo!.rows[r]?.[c] ?? '') : '';
    };
    return [valor('Unidade Consumidora'), valor('Nome/Razão Social')].filter(Boolean).join(' · ') || faturasFiltradas[r]?.arquivo || '';
  };

  const statusRow = (r: number) => faturasFiltradas[r]?.reconciliacao?.status;

  const exportar = () => {
    const stamp = new Date().toLocaleDateString('pt-BR').replace(/\//g, '-');
    const base = (titulo ? titulo.replace(/[^\w.-]+/g, '_') : 'Extracao_Faturas');
    writeWorkbook(edits.grids, `${base}_${stamp}.xlsx`, ucsVigor);
  };

  // ── Salvar as correções ──────────────────────────────────────────────────
  // Fatura sem `extracaoId` (ainda não gravada no banco) não tem onde persistir: a
  // planilha continua editável, mas o que for mexido nela vale só para o Excel desta
  // sessão. Só some o botão quando NENHUMA fatura da tela tem destino.
  const semDestino = !faturasFiltradas.some(f => f.extracaoId);

  /** Junta as pendentes de todas as abas na camada de correção de cada extração.
   *  `celulas` guarda, por extração, quais células entraram — só elas saem de pendente
   *  se a gravação daquela extração der certo. `fora` conta o que não tem destino. */
  const montarPayload = () => {
    const porExtracao = new Map<string, CorrecoesPlanilha>();
    const celulas = new Map<string, { tab: TabKey; r: number; c: number }[]>();
    let fora = 0;
    for (const ed of edits.coletarPendentes()) {
      const owner = source[ed.tab]?.rowOwner?.[ed.r];
      const fatura = owner ? faturasFiltradas[owner.fatura] : undefined;
      if (!owner || !fatura?.extracaoId || !ed.label) { fora++; continue; }
      let atual = porExtracao.get(fatura.extracaoId);
      if (!atual) {
        // Parte da camada que já está no banco: o merge preserva correções de outras
        // células/abas que não foram tocadas agora.
        atual = { versao: VERSAO_CORRECOES, abas: { ...(fatura.correcoes?.abas || {}) } };
        porExtracao.set(fatura.extracaoId, atual);
        celulas.set(fatura.extracaoId, []);
      }
      atual.abas[ed.tab] = { ...(atual.abas[ed.tab] || {}), [chaveCorrecao(owner.chave, ed.c)]: { label: ed.label, valor: ed.valor } };
      celulas.get(fatura.extracaoId)!.push({ tab: ed.tab, r: ed.r, c: ed.c });
    }
    const payload: CorrecaoExtracao[] = [...porExtracao].map(([id, correcoes]) => ({ id, correcoes, escalares: escalaresDeCorrecoes(correcoes) }));
    return { payload, celulas, fora };
  };

  const salvar = async () => {
    if (salvando || edits.pendentesCount === 0) return false;
    const { payload, celulas, fora } = montarPayload();
    if (payload.length === 0) {
      addToast('Essas alterações não têm onde ser salvas: as faturas ainda não estão gravadas no banco.', 'warning');
      return false;
    }
    setSalvando(true);
    try {
      const { salvos, falhas } = await salvarCorrecoesPlanilha(payload, { id: user?.id, nome: profile?.full_name || user?.email });
      if (salvos > 0) {
        const comFalha = new Set(falhas.map(f => f.id));
        const ok = payload.filter(p => !comFalha.has(p.id));
        edits.marcarSalvas(ok.flatMap(p => celulas.get(p.id) || []));
        onCorrecoesSalvas?.(Object.fromEntries(ok.map(p => [p.id, p.correcoes])));
      }
      const sobra = fora > 0 ? ` ${fora} alteração(ões) ficaram de fora (fatura não gravada no banco).` : '';
      if (falhas.length === 0) {
        addToast(`Alterações salvas em ${salvos} fatura(s).${sobra}`, fora > 0 ? 'warning' : 'success');
        return fora === 0;
      }
      addToast(
        salvos > 0
          ? `Salvo em ${salvos} fatura(s); ${falhas.length} falhou(aram): ${falhas[0].erro}${sobra}`
          : `Não foi possível salvar: ${falhas[0].erro}`,
        salvos > 0 ? 'warning' : 'error',
      );
      return false;
    } catch (e: any) {
      addToast(e?.message || 'Falha ao salvar as alterações.', 'error');
      return false;
    } finally { setSalvando(false); }
  };

  /** Executa o que estava represado (o estado pendente já foi salvo ou descartado). */
  const seguir = (acao: { tipo: 'sair' } | { tipo: 'filtro'; alvo: StatusFiltro }) => {
    setConfirmacao(null);
    setInjecoesDaLinha(null);   // o filtro remonta a grade: o índice de linha não vale mais
    if (acao.tipo === 'sair') onClose();
    else setStatusFiltro(acao.alvo);
  };

  const salvarESeguir = async () => { if (confirmacao && await salvar()) seguir(confirmacao); };

  /** Fechar com alteração não salva pede confirmação — antes elas sumiam sem aviso. */
  const tentarFechar = () => {
    if (salvando) return;
    if (edits.pendentesCount > 0) { setConfirmacao({ tipo: 'sair' }); return; }
    onClose();
  };

  const trocarFiltro = (alvo: StatusFiltro) => {
    if (alvo === statusFiltro) return;
    if (edits.pendentesCount > 0) { setConfirmacao({ tipo: 'filtro', alvo }); return; }
    setInjecoesDaLinha(null);
    setStatusFiltro(alvo);
  };

  // ZIP com os PDFs originais das faturas visíveis no filtro atual (clicar de novo cancela).
  const comPdf = faturasFiltradas.filter(f => f.pdfFile || f.pdfPath);
  const baixarZip = async () => {
    if (zipProgresso) { zipCancelRef.current = true; return; }
    if (comPdf.length === 0) return;
    zipCancelRef.current = false;
    setZipProgresso({ feitos: 0, total: comPdf.length });
    try {
      const base = (titulo ? titulo.replace(/[^\w.-]+/g, '_') : 'Faturas');
      await baixarPdfsZip(
        comPdf.map(f => ({ nome: f.arquivo, file: f.pdfFile, path: f.pdfPath })),
        `${base}_PDFs`,
        (feitos, total) => setZipProgresso({ feitos, total }),
        () => zipCancelRef.current,
      );
    } finally { setZipProgresso(null); }
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-stretch justify-center p-2 sm:p-4 animate-fade-in" onClick={tentarFechar}>
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
      <div className="relative bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full h-full flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="px-5 py-3 bg-gradient-to-br from-emerald-600 via-green-600 to-teal-700 flex items-center justify-between gap-4 shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 bg-white/15 rounded-xl flex items-center justify-center border border-white/20 shrink-0">
              <FileSpreadsheet className="w-5 h-5 text-white" />
            </div>
            <div className="min-w-0">
              <h2 className="text-base font-bold text-white truncate">{titulo || 'Planilha da extração'}</h2>
              <p className="text-emerald-100/80 text-xs truncate">
                {faturas.length} fatura(s) · mesmos campos do Excel
                {edits.pendentesCount > 0 && <span className="ml-2 text-amber-200 font-semibold">· {edits.pendentesCount} alteração(ões) não salva(s)</span>}
              </p>
            </div>
          </div>
          <button onClick={tentarFechar} title="Fechar" className="w-8 h-8 bg-white/15 rounded-xl flex items-center justify-center text-white/80 hover:text-white hover:bg-white/25 transition-all shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Toolbar */}
        <div className="px-4 py-2.5 border-b border-gray-100 dark:border-gray-800 flex flex-wrap items-center gap-2 shrink-0">
          {/* Abas */}
          <div className="flex items-center gap-1">
            {available.map(t => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`px-3 py-1.5 text-[12px] font-semibold rounded-lg transition-colors ${activeTab === t.key ? 'bg-primary text-white' : 'text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800'}`}
              >
                {t.label} <span className="opacity-70">({linhasDe(t.key)})</span>
              </button>
            ))}
          </div>

          <div className="flex-1" />

          {/* Filtro de status (só faz sentido nas abas de 1 linha por fatura) */}
          {(activeTab === 'faturas' || activeTab === 'resumo') && (
            <div className="flex items-center gap-1">
              {FILTROS.map(({ key, rotulo, classe }) => {
                if (key !== 'todos' && contagens[key] === 0) return null;
                const ativo = statusFiltro === key;
                return (
                  <button
                    key={key}
                    onClick={() => trocarFiltro(key)}
                    className={`text-[11px] font-semibold px-2.5 py-1 rounded-full border transition-colors ${ativo ? 'bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 border-gray-900 dark:border-gray-100' : `bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 ${classe}`}`}
                  >
                    {rotulo} ({key === 'todos' ? contagens.todos : contagens[key]})
                  </button>
                );
              })}
            </div>
          )}

          {/* Editar */}
          <button
            onClick={() => setEditable(e => !e)}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-medium rounded-lg border transition-colors ${editable ? 'bg-amber-500 border-amber-500 text-white' : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'}`}
          >
            {editable ? <Pencil className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
            {editable ? 'Editando' : 'Só leitura'}
          </button>

          {edits.salvasCount > 0 && (
            <span title="Células com correção manual já gravada no banco (contorno verde na planilha)" className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[12px] font-medium rounded-lg bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800">
              <Check className="w-3.5 h-3.5" /> {edits.salvasCount} salva(s)
            </span>
          )}

          {edits.pendentesCount > 0 && (
            <button onClick={edits.resetAll} disabled={salvando} title="Descartar as alterações que ainda não foram salvas" className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[12px] font-medium rounded-lg border border-gray-200 dark:border-gray-700 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-50">
              <RotateCcw className="w-3.5 h-3.5" /> Descartar {edits.pendentesCount}
            </button>
          )}

          {edits.pendentesCount > 0 && (
            <button
              onClick={salvar}
              disabled={salvando || semDestino}
              title={semDestino ? 'Estas faturas ainda não estão gravadas no banco — as alterações valem só para o Excel exportado agora.' : 'Gravar as alterações no banco'}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-semibold rounded-lg bg-amber-500 hover:bg-amber-600 text-white disabled:opacity-50"
            >
              {salvando ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
              Salvar alterações ({edits.pendentesCount})
            </button>
          )}

          {comPdf.length > 0 && (
            <button
              onClick={baixarZip}
              title={zipProgresso ? 'Cancelar o download do ZIP' : 'Baixar os PDFs originais das faturas do filtro atual num .zip'}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-medium rounded-lg border ${zipProgresso
                ? 'border-red-200 dark:border-red-800 text-red-600 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20'
                : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'}`}
            >
              {zipProgresso ? <><X className="w-3.5 h-3.5" /> {zipProgresso.feitos}/{zipProgresso.total} · Cancelar</> : <><FileArchive className="w-3.5 h-3.5" /> PDFs (ZIP)</>}
            </button>
          )}

          <button onClick={exportar} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-semibold rounded-lg bg-green-600 hover:bg-green-700 text-white">
            <Download className="w-3.5 h-3.5" /> Exportar Excel{edits.editedCount > 0 ? ' (com correções)' : ''}
          </button>
        </div>

        {editable && noResumo && (
          <div className="px-4 py-1.5 bg-gray-50 dark:bg-gray-800/40 border-b border-gray-100 dark:border-gray-800 flex items-center gap-2 text-[11px] text-gray-600 dark:text-gray-300 shrink-0">
            <Info className="w-3.5 h-3.5 shrink-0" />
            O Resumo repete campos da aba Faturas: o que você corrigir aqui é gravado lá, na mesma célula.{' '}
            {ucsVigor
              ? 'A injeção é somada por origem (usina da Vigor, de terceiro, ou sem UC na fatura): com uma parcela só, a célula é editável; com mais de uma, o clique abre a composição para editar cada parcela.'
              : 'A injeção é um total calculado — não foi possível carregar as UCs das usinas da Vigor para separar por origem.'}
          </div>
        )}

        {editable && !noResumo && (
          <div className="px-4 py-1.5 bg-amber-50 dark:bg-amber-900/10 border-b border-amber-100 dark:border-amber-900/30 flex items-center gap-2 text-[11px] text-amber-700 dark:text-amber-300 shrink-0">
            <Info className="w-3.5 h-3.5 shrink-0" />
            {semDestino
              ? 'Estas faturas ainda não estão gravadas no banco: as edições valem só para o Excel exportado agora. Cada aba é independente — editar “Valor” em Faturas não recalcula a aba Itens.'
              : 'As alterações só ficam guardadas depois de “Salvar alterações” — elas são gravadas como correção manual, por cima do que o robô extraiu. Cada aba é independente — editar “Valor” em Faturas não recalcula a aba Itens.'}
          </div>
        )}

        {/* Grid */}
        <div className="flex-1 min-h-0 p-3">
          {grid ? (
            <SpreadsheetGrid
              key={activeTab}
              columns={grid.columns}
              groupHeaders={grid.groupHeaders}
              rows={grid.rows}
              editable={editable}
              edited={projetar(edits.editedFor(abaEdicao))}
              pendentes={projetar(edits.pendentesFor(abaEdicao))}
              onCellChange={(r, c, raw) => {
                const alvo = noResumo ? alvoDaCelula(r, c) : c;
                if (alvo !== null) edits.onCellChange(abaEdicao, r, alvo, raw);
              }}
              celulaEditavel={noResumo ? ((r, c) => alvoDaCelula(r, c) !== null) : undefined}
              acaoCelula={noResumo ? acaoDaCelula : undefined}
              dicaAcao="Clique para abrir a composição da injeção desta fatura (parcela a parcela, com a UC geradora)"
              freezeCount={cfg.freezeCount}
              freezeWidths={cfg.freezeWidths}
              statusRow={cfg.rail ? statusRow : undefined}
              severityCol={grid.severityCol}
              highlightCol={grid.highlightCol}
            />
          ) : (
            <div className="h-full flex items-center justify-center text-gray-400 text-sm">Nenhum dado para exibir neste filtro.</div>
          )}
        </div>

        {/* Composição da injeção da linha: soma por origem no Resumo, parcela a parcela aqui. */}
        {injecoesDaLinha !== null && resumo && (
          <InjecoesFaturaModal
            titulo={rotuloDaLinha(injecoesDaLinha)}
            parcelas={parcelasDaLinha(injecoesDaLinha)}
            ucsVigor={ucsVigor}
            editavel={editable}
            onClose={() => setInjecoesDaLinha(null)}
            onAplicar={mudancas => {
              for (const m of mudancas) edits.onCellChange('faturas', injecoesDaLinha, m.col, m.valor);
              setInjecoesDaLinha(null);
            }}
          />
        )}

        {/* Confirmação — o X fechava (e o filtro remontava a grade) levando junto tudo
            o que não tinha sido salvo, sem aviso nenhum. */}
        {confirmacao && (
          <div className="absolute inset-0 z-10 flex items-center justify-center p-4 bg-black/40 backdrop-blur-[2px]" onClick={() => setConfirmacao(null)}>
            <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-md p-5" onClick={e => e.stopPropagation()}>
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-xl bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center shrink-0">
                  <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                </div>
                <div className="min-w-0">
                  <h3 className="text-sm font-bold text-gray-800 dark:text-gray-100">
                    {confirmacao.tipo === 'sair' ? 'Sair sem salvar?' : 'Trocar o filtro sem salvar?'}
                  </h3>
                  <p className="text-[13px] text-gray-600 dark:text-gray-300 mt-1">
                    {edits.pendentesCount} alteração(ões) ainda não foram salvas. Se {confirmacao.tipo === 'sair' ? 'sair' : 'trocar o filtro'} agora, elas se perdem.
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap items-center justify-end gap-2 mt-5">
                <button onClick={() => setConfirmacao(null)} disabled={salvando} className="px-3 py-1.5 text-[12px] font-medium rounded-lg border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-50">
                  Continuar editando
                </button>
                <button onClick={() => { const acao = confirmacao; edits.resetAll(); seguir(acao); }} disabled={salvando} className="px-3 py-1.5 text-[12px] font-medium rounded-lg border border-red-200 dark:border-red-800 text-red-600 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50">
                  {confirmacao.tipo === 'sair' ? 'Sair sem salvar' : 'Trocar sem salvar'}
                </button>
                <button onClick={salvarESeguir} disabled={salvando || semDestino} title={semDestino ? 'Estas faturas ainda não estão gravadas no banco.' : undefined} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-semibold rounded-lg bg-green-600 hover:bg-green-700 text-white disabled:opacity-50">
                  {salvando ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                  {confirmacao.tipo === 'sair' ? 'Salvar e sair' : 'Salvar e trocar'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
};

export default PlanilhaFaturasModal;
